"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import { Button } from "~/components/ui/button";
import { reportClientError } from "~/lib/observability-client";

/**
 * P0-16 · Route-segment error boundary. Reports the unhandled client error to the
 * server intake (which attaches store/requestId and scrubs PII) and renders a
 * store-themed fallback with a retry.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    reportClientError({
      message: error.message,
      stack: error.stack,
      name: error.name,
      digest: error.digest,
      route: pathname,
    });
  }, [error, pathname]);

  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-bg px-4 text-center text-fg">
      <div className="max-w-md space-y-2">
        <h1 className="font-heading text-2xl font-bold">Something went wrong</h1>
        <p className="text-fg-muted">
          An unexpected error occurred. It has been logged and we&apos;re looking
          into it. You can try again.
        </p>
      </div>
      <Button variant="primary" onClick={reset}>
        Try again
      </Button>
    </main>
  );
}
